import React, { useContext, memo, FC } from 'react';
import {
  makeStyles, mergeClasses, Button, Text, tokens, shorthands, MenuTrigger, MenuPopover, Menu, MenuList,
  MenuItem,
} from '@fluentui/react-components';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import {
  Dismiss24Regular, ChatAdd24Regular, PersonSupport24Regular, MoreHorizontal20Regular,
} from '@fluentui/react-icons';

const useHeaderStyles = makeStyles({
  root: {
    height: '59px',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
  },
  divider: {
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke1,
    borderBottomWidth: '1px',
  },
  leftPanel: {
    ...shorthands.flex(1),
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    marginLeft: tokens.spacingHorizontalS,
    marginRight: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    marginTop: 0,
    marginBottom: 0,
    display: 'table',
  },
  smallTitle: {
    marginLeft: tokens.spacingHorizontalS,
    marginRight: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    marginTop: 0,
    marginBottom: 0,
    display: 'table',
  },
  label: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase100,
    backgroundColor: tokens.colorNeutralBackground4,
    lineHeight: tokens.lineHeightBase200,
    verticalAlign: 'middle',
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    'border-radius': tokens.borderRadiusLarge,
    marginLeft: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalSNudge,
    marginTop: tokens.spacingVerticalSNudge,
    display: 'inline-block',
  },
  copilotIcon: {
    paddingLeft: tokens.spacingHorizontalNone,
    paddingRight: tokens.spacingHorizontalNone,
    paddingTop: tokens.spacingVerticalSNudge,
  },
});

export interface IHeaderProps {
  title?: React.ReactNode;
  showPreviewTag?: boolean;
  showSupportButton?: boolean;
  showRefreshButton?: boolean;
  onSupportClick?(): void;
  onRefresh?(): void;
  onClose?(): void;
  children?: React.ReactNode;
  onChatHistoryClick?(): void;
  isChatHistoryEnabled?: boolean;
  isUxImprovementsEnabled?: boolean;
}

export const Header: FC<IHeaderProps> = memo(({
  showPreviewTag,
  showSupportButton,
  showRefreshButton,
  children,
  title,
  onSupportClick,
  onRefresh,
  onClose,
  onChatHistoryClick,
  isChatHistoryEnabled,
}) => {
  const styles = useHeaderStyles();
  const i18n = useContext(I18nContext) as I18nModel;
  const closeBtnLabel = i18n.getString(_TL_('Close'));
  const closeBtnAriaLabel = i18n.getString(_TL_('Close Copilot Chat'));
  const refreshBtnLabel = i18n.getString(_TL_('Start over'));
  const refreshBtnAriaLabel = i18n.getString(_TL_('Start over Copilot Chat'));
  const supportBtnLabel = i18n.getString(_TL_('Connect to support'));
  const headerId = 'copilot-chat-header-id';

  return (
    <div className={mergeClasses(styles.divider, styles.root, 'ads-copilot-header')}>
      <div className={styles.leftPanel}>
        {children || (
          <>
            <h3 className={mergeClasses(styles.smallTitle, 'title')} id={headerId}>
              {title || i18n.getString(_TL_('Copilot'))}
              {showPreviewTag && <Text className={styles.label} weight="semibold">{i18n.getString(_TL_('PREVIEW'))}</Text>}
            </h3>
          </>
        )}
      </div>
      { isChatHistoryEnabled
      && (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button
            className="scope-filters-overflow-trigger"
            icon={<MoreHorizontal20Regular />}
            appearance="subtle"
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem id="chat-history-menu" onClick={onChatHistoryClick}>
              {i18n.getString(_TL_('Chat History'))}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
      )}
      {
        <>
          { showRefreshButton && <Button className="refresh-button" onClick={onRefresh} title={refreshBtnLabel} aria-label={refreshBtnAriaLabel} aria-describedby={headerId} icon={<ChatAdd24Regular />} appearance="transparent" />}
          { showSupportButton && <Button className="support-button" onClick={onSupportClick} title={supportBtnLabel} aria-label={supportBtnLabel} aria-describedby={headerId} icon={<PersonSupport24Regular />} appearance="transparent" />}
        </>
      }
      <Button className="close-button" title={closeBtnLabel} aria-label={closeBtnAriaLabel} aria-describedby={headerId} icon={<Dismiss24Regular />} appearance="transparent" onClick={onClose} />
    </div>
  );
});

Header.defaultProps = {
  onClose: undefined,
  showPreviewTag: true,
  showSupportButton: false,
  showRefreshButton: true,
  onRefresh: undefined,
  children: undefined,
  title: undefined,
  isChatHistoryEnabled: false,
};
